import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from './useSupabase';
import type {
  CreateReservedCrewNumberDto,
  ReservedCrewNumberViewRow,
  ReservedCrewNumberWithProduct,
} from '@/types/reservedCrewNumber';

interface UseReservedCrewNumbersReturn {
  reservations: ReservedCrewNumberWithProduct[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  create: (dto: CreateReservedCrewNumberDto) => Promise<ReservedCrewNumberWithProduct | null>;
  remove: (id: string) => Promise<boolean>;
}

export function useReservedCrewNumbers(): UseReservedCrewNumbersReturn {
  const supabase = useSupabase();
  const [reservations, setReservations] = useState<ReservedCrewNumberWithProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('reserved_crew_numbers_view')
      .select('*')
      .order('product_id', { ascending: true })
      .order('crew_number_int', { ascending: true });

    if (fetchError) {
      setError(new Error(fetchError.message));
      setReservations([]);
    } else {
      const rows = (data as ReservedCrewNumberViewRow[] | null) ?? [];
      setReservations(
        rows.map((row) => ({
          id: row.id,
          product_id: row.product_id,
          crew_number_int: row.crew_number_int,
          reason: row.reason,
          created_by: row.created_by,
          created_at: row.created_at,
          product: {
            id: row.product_id,
            name: row.product_name,
            code: row.product_code,
          },
          crew: row.crew_id
            ? {
                id: row.crew_id,
                name: row.crew_name,
                crew_number: row.crew_crew_number,
                status: row.crew_status,
              }
            : null,
        }))
      );
    }
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const create = useCallback(
    async (dto: CreateReservedCrewNumberDto): Promise<ReservedCrewNumberWithProduct | null> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { data, error: insertError } = await supabase
        .from('reserved_crew_numbers')
        .insert({ ...dto, created_by: user?.id || null })
        .select('*, product:products(id, name, code)')
        .single();

      if (insertError) {
        throw new Error(insertError.message);
      }
      await fetchReservations();
      return data as ReservedCrewNumberWithProduct;
    },
    [supabase, fetchReservations]
  );

  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      const { error: deleteError } = await supabase
        .from('reserved_crew_numbers')
        .delete()
        .eq('id', id);

      if (deleteError) {
        throw new Error(deleteError.message);
      }
      await fetchReservations();
      return true;
    },
    [supabase, fetchReservations]
  );

  return {
    reservations,
    loading,
    error,
    refresh: fetchReservations,
    create,
    remove,
  };
}
